import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Plus, Pencil, Trash2, RotateCw,
  Search, Eye, BookOpen, Clock,
  ChevronRight, BrainCircuit, Timer, Trophy,
  ListChecks, ChevronLeft
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useListState } from "@/hooks/useListState";
import {
  useGetTestsQuery,
  useCreateTestMutation,
  useUpdateTestMutation,
  useDeleteTestMutation,
  Test
} from "@/redux/slices/testApi";
import { useGetChaptersQuery } from "@/redux/slices/chapterApi";
import { useGetSubjectsQuery } from "@/redux/slices/subjectApi";
import { useGetAcademicYearsQuery } from "@/redux/slices/academicYearApi";

const schema = z.object({
  title: z.string().trim().min(2, "Title required"),
  description: z.string().max(500).optional().default(""),
  timeLimit: z.coerce.number().min(1, "Required"),
  totalMarks: z.coerce.number().min(1, "Required"),
  passingMarks: z.coerce.number().min(1, "Required"),
  academicYearId: z.string().min(1, "Year required"),
  subjectId: z.string().min(1, "Subject required"),
  chapterId: z.string().min(1, "Chapter required"),
  isActive: z.boolean().default(true),
});
type FormValues = z.infer<typeof schema>;

export default function ChapterTestsPage() {
  const list = useListState();
  const navigate = useNavigate();

  const { data, isFetching, refetch } = useGetTestsQuery(list.queryParams());
  const { data: yearsData } = useGetAcademicYearsQuery({ limit: 100 });
  const { data: subjectsData } = useGetSubjectsQuery({ limit: 1000 });
  const { data: chaptersData } = useGetChaptersQuery({ limit: 1000 });

  const [createTest, createM] = useCreateTestMutation();
  const [updateTest, updateM] = useUpdateTestMutation();
  const [deleteTest, deleteM] = useDeleteTestMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Test | null>(null);
  const [toDelete, setToDelete] = useState<Test | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "", description: "", timeLimit: 30, totalMarks: 10,
      passingMarks: 4, academicYearId: "", subjectId: "",
      chapterId: "", isActive: true
    },
  });

  const watchYear = form.watch("academicYearId");
  const watchSubject = form.watch("subjectId");

  const filteredSubjects = useMemo(() => {
    if (!subjectsData?.data || !watchYear) return [];
    return subjectsData.data.filter(s => s.academicYearId === watchYear);
  }, [subjectsData, watchYear]);

  const filteredChapters = useMemo(() => {
    if (!chaptersData?.data || !watchSubject) return [];
    return chaptersData.data.filter(c => c.subjectId === watchSubject);
  }, [chaptersData, watchSubject]);

  const openCreate = () => {
    setEditing(null);
    form.reset({
      title: "", description: "", timeLimit: 30, totalMarks: 10,
      passingMarks: 4, academicYearId: "", subjectId: "",
      chapterId: "", isActive: true
    });
    setOpen(true);
  };

  const openEdit = (t: Test) => {
    setEditing(t);
    form.reset(t as any);
    setOpen(true);
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateTest({ id: editing.id, body: v }).unwrap();
        toast.success("Assessment criteria updated");
      } else {
        await createTest(v).unwrap();
        toast.success("New chapter test initiated");
      }
      setOpen(false);
    } catch {
      toast.error("Failed to store assessment details");
    }
  };

  return (
    <div className="min-h-screen bg-muted/20 pb-12">
      <PageHeader
        title="Chapter Tests"
        description="Design and manage interactive chapter-level examinations"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={openCreate} className="shadow-lg shadow-primary/20">
              <Plus className="mr-1.5 h-4 w-4" /> Create Test
            </Button>
          </div>
        }
      />

      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar
            value={list.search}
            onChange={list.setSearch}
            placeholder="Search assessments..."
            className="flex-1"
          />
          <div className="flex flex-wrap items-center gap-3">
            <Select value={list.filters.academicYearId || "all"} onValueChange={(v) => list.setFilter("academicYearId", v === "all" ? undefined : v)}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {yearsData?.data?.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isFetching ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-72 animate-pulse rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {data?.data?.map((test) => (
              <Card key={test.id} className="group relative overflow-hidden bg-card/60 backdrop-blur-sm shadow-sm ring-1 ring-border hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300">
                <div className="absolute top-0 left-0 h-1 w-full bg-primary/20 transition-all group-hover:bg-primary" />

                <div className="relative h-32 bg-primary/5 flex items-center justify-center p-6 overflow-hidden">
                  <BrainCircuit className="h-24 w-24 text-primary/5 absolute -right-4 -bottom-4 rotate-12 transition-transform group-hover:scale-110" />
                  <div className="text-center space-y-2 z-10">
                    <div className="mx-auto w-10 h-10 rounded-xl bg-white/80 shadow-sm flex items-center justify-center transition-transform group-hover:rotate-6">
                      <BookOpen className="h-5 w-5 text-primary" />
                    </div>
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-none font-bold text-[10px] uppercase tracking-wider">{test.chapter?.name}</Badge>
                  </div>
                </div>

                <CardHeader className="p-6 pb-2">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <CardTitle className="text-lg font-bold leading-tight group-hover:text-primary transition-colors line-clamp-1">{test.title}</CardTitle>
                      <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">{test.subject?.name}</p>
                    </div>
                    <div className="flex shrink-0 gap-1 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full hover:bg-primary/10" onClick={() => openEdit(test)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive" onClick={() => setToDelete(test)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="px-6 pb-4">
                  <div className="grid grid-cols-3 gap-2 py-3 border-y border-dashed border-border/60">
                    <div className="text-center">
                      <p className="text-[9px] text-muted-foreground uppercase font-black tracking-tighter">Time</p>
                      <p className="text-xs font-bold flex items-center justify-center gap-1">
                        <Clock className="h-3 w-3 text-primary" /> {test.timeLimit}m
                      </p>
                    </div>
                    <div className="text-center border-x">
                      <p className="text-[9px] text-muted-foreground uppercase font-black tracking-tighter">Marks</p>
                      <p className="text-xs font-bold">{test.totalMarks}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[9px] text-muted-foreground uppercase font-black tracking-tighter">Target</p>
                      <p className="text-xs font-bold flex items-center justify-center gap-1">
                        <Trophy className="h-3 w-3 text-yellow-500" /> {test.passingMarks}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted text-primary">
                        <ListChecks className="h-3 w-3" />
                      </div>
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{test.questions?.length || 0} MCQ Items</span>
                    </div>
                    <StatusBadge status={test.isActive ? "active" : "inactive"} />
                  </div>
                </CardContent>

                <CardFooter className="px-6 pb-6 pt-0">
                  <Button className="w-full gap-2 rounded-xl h-10 font-bold uppercase tracking-widest text-[10px] shadow-sm group/btn" variant="secondary" onClick={() => navigate(`/chapter-tests/${test.id}/questions`)}>
                    Manage Question Set <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover/btn:translate-x-1" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isFetching && data && data.total > 0 && (
          <div className="flex flex-col items-center justify-between gap-6 border-t pt-8 sm:flex-row">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Page {list.page} of {Math.ceil(data.total / list.limit)} • {data.total} Tests Total</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-xl h-9 shadow-sm" disabled={list.page === 1} onClick={() => list.setPage(list.page - 1)}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              <Button variant="outline" size="sm" className="rounded-xl h-9 shadow-sm" disabled={list.page >= Math.ceil(data.total / list.limit)} onClick={() => list.setPage(list.page + 1)}>
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {!isFetching && data?.total === 0 && (
          <div className="flex flex-col items-center justify-center rounded-[2.5rem] border-2 border-dashed border-primary/10 py-24 bg-card/50 backdrop-blur-sm shadow-inner">
            <div className="p-6 rounded-full bg-primary/5 mb-6 ring-1 ring-primary/10">
              <BrainCircuit className="h-16 w-16 text-primary/30" />
            </div>
            <h3 className="text-xl font-black text-foreground/80 tracking-tight">No Tests Found</h3>
            <p className="text-sm text-muted-foreground max-w-xs text-center mt-2 leading-relaxed">
              Begin building your assessment repository by creating the first chapter-specific MCQ test.
            </p>
            <Button onClick={openCreate} className="mt-8 rounded-xl h-11 px-8 shadow-xl shadow-primary/20">
              <Plus className="mr-2 h-4 w-4" /> Create Initial Test
            </Button>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl shadow-2xl border-none p-0 flex flex-col rounded-2xl max-h-[90vh]">
          <DialogHeader className="p-8 pb-4 bg-primary/5 text-left shrink-0">
            <DialogTitle className="text-2xl font-bold">{editing ? "Refine Assessment" : "New Chapter Assessment"}</DialogTitle>
            <DialogDescription className="text-sm font-medium text-muted-foreground">Configure global test parameters and structural metadata.</DialogDescription>
          </DialogHeader>

          <div className="px-8 py-6 overflow-y-auto flex-1">
            <form id="test-form" onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-2">
                <Label className="text-sm font-semibold text-foreground">Assessment Title</Label>
                <Input className="h-11 shadow-sm" placeholder="e.g. Fundamental Ayurvedic Principles - Test A" {...form.register("title")} />
                {form.formState.errors.title && <p className="text-xs font-bold text-destructive">{form.formState.errors.title.message}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Time Limit (Min)</Label>
                <Input type="number" className="h-11 shadow-sm font-mono" {...form.register("timeLimit")} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Total Marks</Label>
                <Input type="number" className="h-11 shadow-sm font-mono" {...form.register("totalMarks")} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Passing Marks</Label>
                <Input type="number" className="h-11 shadow-sm font-mono" {...form.register("passingMarks")} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Academic Year</Label>
                <Select value={form.watch("academicYearId") || undefined} onValueChange={(v) => { form.setValue("academicYearId", v); form.setValue("subjectId", ""); form.setValue("chapterId", ""); }}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Year" /></SelectTrigger>
                  <SelectContent>{yearsData?.data?.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Subject</Label>
                <Select value={form.watch("subjectId") || undefined} onValueChange={(v) => { form.setValue("subjectId", v); form.setValue("chapterId", ""); }} disabled={!watchYear}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Subject" /></SelectTrigger>
                  <SelectContent>{filteredSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Target Chapter</Label>
                <Select value={form.watch("chapterId") || undefined} onValueChange={(v) => form.setValue("chapterId", v)} disabled={!watchSubject}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Chapter" /></SelectTrigger>
                  <SelectContent>{filteredChapters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Status</Label>
                <Select
                  value={form.watch("isActive") ? "true" : "false"}
                  onValueChange={(v) => form.setValue("isActive", v === "true")}
                >
                  <SelectTrigger className="h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Published (Active)</SelectItem>
                    <SelectItem value="false">Hidden (Inactive)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label className="text-sm font-semibold text-foreground">Description / Coverage</Label>
                <Textarea rows={3} className="resize-none shadow-sm" placeholder="Outline specific topics covered in this assessment..." {...form.register("description")} />
              </div>
            </form>
          </div>

          <DialogFooter className="p-8 border-t bg-muted/10 shrink-0">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 px-8 font-bold uppercase tracking-tight text-xs">Cancel</Button>
            <Button form="test-form" type="submit" disabled={createM.isLoading || updateM.isLoading} className="h-11 px-8 rounded-xl font-bold uppercase tracking-widest text-xs shadow-xl shadow-primary/20 group">
              {editing ? "Save Test" : "Create Test"}
              <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            await deleteTest(toDelete.id).unwrap();
            toast.success("Assessment permanently deleted");
            setToDelete(null);
          }
        }}
        loading={deleteM.isLoading}
        title="Purge Assessment?"
        description={`"${toDelete?.title}" and all its question linkages will be permanently removed.`}
      />
    </div>
  );
}
